// A room: one world, one module, one tick loop's worth of state. The room is the authority.
// It knows nothing about sockets; server.ts drives it and ships the snapshots it builds.

import type { Entity, EntityDef, GameModule, Input, World } from "../contract.js";
import { makeRng, fnv1a, type Rng } from "../shared/rng.js";
import { SpatialHash } from "../shared/spatial.js";
import { coerceInput, type SnapshotMsg, type EntityPatch } from "../shared/protocol.js";

/** All world state lives here, in one serialisable object (no persistence yet; keep the door open). */
export interface WorldState {
  tick: number;
  /** Simulated seconds since the room started. */
  time: number;
  nextId: number;
  /** Keyed by id. Integer keys iterate in ascending order, which keeps module iteration deterministic. */
  entities: Record<number, Entity>;
  players: Record<string, PlayerRecord>;
}

export interface PlayerRecord {
  /** Recorded when the player arrives; never resolved at display time. */
  name: string;
  /** The player's own entity (first entity spawned with this owner), if any. */
  eid?: number;
}

export interface RoomOptions {
  id: string;
  seed: number;
  /** Spatial hash cell size in world units. */
  cellSize?: number;
}

/** Marker for a static entity the client has been sent: nothing to diff, so nothing to remember. */
const STATIC_SENT: Record<string, unknown> = Object.freeze({});

/** Per-client replication state: what this client has been told so far. */
export class ClientView {
  seq = 0;
  /**
   * Last values sent per entity id. Entities not present here are unknown to the client.
   * Static entities map to the shared STATIC_SENT marker.
   */
  readonly sent = new Map<number, Record<string, unknown>>();
  readonly namesSeen = new Set<string>();
  lbKey = "";
  /** Bytes of snapshot JSON handed to this client, for metrics. */
  bytes = 0;
}

const hasOwn = Object.prototype.hasOwnProperty;

export class Room {
  readonly id: string;
  readonly module: GameModule;
  readonly seed: number;
  /** Fixed simulation step in seconds. */
  readonly dt: number;
  readonly state: WorldState;
  readonly rng: Rng;
  /** Wall-clock milliseconds the last step() took (simulation only, not snapshots). */
  lastStepMs = 0;

  private readonly spatial: SpatialHash<Entity>;
  private readonly inputs = new Map<string, Input>();
  private readonly world: World;
  private ticking = false;

  constructor(module: GameModule, opts: RoomOptions) {
    this.id = opts.id;
    this.module = module;
    this.seed = opts.seed;
    this.dt = 1 / module.meta.tickRate;
    this.rng = makeRng(opts.seed);
    this.spatial = new SpatialHash<Entity>(opts.cellSize ?? 256);
    this.state = { tick: 0, time: 0, nextId: 1, entities: {}, players: {} };
    this.world = this.makeWorld();
    module.init(this.world, this.rng);
    this.spatial.rebuild(this.liveEntities());
  }

  // ---- players -------------------------------------------------------------------------------

  get playerCount(): number {
    return Object.keys(this.state.players).length;
  }

  players(): string[] {
    return Object.keys(this.state.players);
  }

  hasPlayer(playerId: string): boolean {
    return hasOwn.call(this.state.players, playerId);
  }

  join(playerId: string, name: string): void {
    if (this.hasPlayer(playerId)) throw new Error(`join: player ${playerId} already in room`);
    if (this.playerCount >= this.module.meta.players.max) throw new Error("join: room is full");
    this.state.players[playerId] = { name };
    this.module.onJoin(this.world, playerId, this.rng);
  }

  leave(playerId: string): void {
    if (!this.hasPlayer(playerId)) return;
    this.inputs.delete(playerId);
    try {
      this.module.onLeave(this.world, playerId);
    } finally {
      // Whatever the module did, nothing owned by a departed player may linger as a ghost.
      for (const e of this.liveEntities()) if (e.owner === playerId) delete this.state.entities[e.id];
      delete this.state.players[playerId];
    }
  }

  /** Accept an untrusted input object for a player. */
  setInput(playerId: string, raw: unknown): void {
    if (!this.hasPlayer(playerId)) return;
    this.inputs.set(playerId, coerceInput(this.module.inputSchema, raw));
  }

  /** Forget a player's input (e.g. while disconnected): their entity ticks with `input === undefined`. */
  clearInput(playerId: string): void {
    this.inputs.delete(playerId);
  }

  playerEntity(playerId: string): Entity | undefined {
    const eid = this.state.players[playerId]?.eid;
    return eid === undefined ? undefined : this.state.entities[eid];
  }

  // ---- simulation ----------------------------------------------------------------------------

  /** Advance the world by one fixed step. Throws if the module throws; the caller decides what dies. */
  step(): void {
    if (this.ticking) throw new Error("step: re-entrant tick");
    this.ticking = true;
    const t0 = performance.now();
    try {
      const { entities } = this.state;
      this.spatial.rebuild(this.liveEntities());
      // Snapshot the id list so entities spawned this tick (by the world tick or by another
      // entity) first act next tick.
      const ids = Object.keys(entities);
      // The module's world-level tick runs first, before any entity acts.
      if (this.module.tick) this.module.tick(this.world, this.dt, this.rng);
      for (let i = 0; i < ids.length; i++) {
        const e = entities[ids[i] as unknown as number];
        if (!e) continue; // despawned earlier this tick
        const def = this.module.entities.get(e.type);
        if (!def?.tick) continue;
        const input = e.owner !== undefined ? this.inputs.get(e.owner) : undefined;
        def.tick(e, this.world, input, this.dt, this.rng);
      }
      this.state.tick++;
      this.state.time = this.state.tick * this.dt;
    } finally {
      this.ticking = false;
      this.lastStepMs = performance.now() - t0;
    }
  }

  /** Fingerprint of the whole world, for determinism checks. */
  hash(): string {
    return fnv1a(JSON.stringify(this.state) + "|" + this.rng.state());
  }

  /** Approximate serialised size of the world in bytes. */
  stateBytes(): number {
    return JSON.stringify(this.state).length;
  }

  liveEntities(): Entity[] {
    return Object.values(this.state.entities);
  }

  // ---- replication ---------------------------------------------------------------------------

  /**
   * The rectangle this player can see, in world units. Grows with the player's radius so a big
   * blob still sees its surroundings. Spectators (no entity) see the middle of the world.
   */
  viewRect(playerId: string): { x0: number; y0: number; x1: number; y1: number } {
    const { w, h } = this.module.meta.world;
    const self = this.playerEntity(playerId);
    const zoom = self ? Math.min(6, Math.max(1, Math.sqrt(self.r / 25))) : 2;
    const cx = self ? self.x : w / 2;
    const cy = self ? self.y : h / 2;
    const hw = 960 * zoom;
    const hh = 600 * zoom;
    return { x0: cx - hw, y0: cy - hh, x1: cx + hw, y1: cy + hh };
  }

  /**
   * Build the next snapshot for one client: only entities in view, only fields that changed
   * since this client last heard about them, plus removals, name arrivals and the leaderboard.
   */
  snapshot(playerId: string, view: ClientView, serverTimeMs: number): SnapshotMsg {
    const rect = this.viewRect(playerId);
    const visible = this.spatial.queryRect(rect.x0, rect.y0, rect.x1, rect.y1);
    const { entities, players } = this.state;
    const self = this.playerEntity(playerId);

    const e: Record<string, EntityPatch> = {};
    let changed = 0;
    const seen = new Set<number>();
    const consider = (ent: Entity) => {
      if (entities[ent.id] !== ent) return; // despawned after the index was built
      seen.add(ent.id);
      let last = view.sent.get(ent.id);
      if (last === STATIC_SENT) return; // a static entity, sent once already; by contract nothing has changed
      const def = this.module.entities.get(ent.type)!;
      const patch: EntityPatch = {};
      let dirty = false;
      if (!last) {
        // A static entity is sent whole, once; nothing is remembered for a later diff.
        last = {};
        view.sent.set(ent.id, def.static ? STATIC_SENT : last);
        patch._t = ent.type;
        if (ent.owner !== undefined) patch._o = ent.owner;
        dirty = true;
      }
      for (const f of def.replicated) {
        const v = quantise(ent[f]);
        if (last[f] !== v) {
          last[f] = v;
          patch[f] = v;
          dirty = true;
        }
      }
      if (dirty) {
        e[ent.id] = patch;
        changed++;
      }
    };
    for (const ent of visible) consider(ent);
    // Entities spawned this tick are not in the index yet; your own must never be late.
    if (self && !seen.has(self.id)) consider(self);

    const rm: number[] = [];
    for (const id of view.sent.keys()) {
      if (!seen.has(id)) {
        rm.push(id);
        view.sent.delete(id);
      }
    }

    let pn: Record<string, string | null> | undefined;
    for (const pid in players) {
      if (!view.namesSeen.has(pid)) {
        view.namesSeen.add(pid);
        (pn ??= {})[pid] = players[pid].name;
      }
    }
    for (const pid of view.namesSeen) {
      if (!hasOwn.call(players, pid)) {
        view.namesSeen.delete(pid);
        (pn ??= {})[pid] = null;
      }
    }

    const msg: SnapshotMsg = {
      t: "s",
      seq: ++view.seq,
      time: serverTimeMs,
      tick: this.state.tick,
      n: this.playerCount,
    };
    if (self) msg.you = self.id;
    if (changed) msg.e = e;
    if (rm.length) msg.rm = rm;
    if (pn) msg.pn = pn;

    const lb = this.leaderboard();
    if (lb) {
      const key = lb.map((p) => p[0] + ":" + p[1]).join(",");
      if (key !== view.lbKey) {
        view.lbKey = key;
        msg.lb = lb;
      }
    }
    return msg;
  }

  /** Top ten players by meta.score, integer scores, refreshed twice a second. */
  private lbCache: { tick: number; value: [string, number][] | null } = { tick: -1, value: null };
  leaderboard(): [string, number][] | null {
    const field = this.module.meta.score;
    if (!field) return null;
    const every = Math.max(1, Math.round(this.module.meta.tickRate / 2));
    const bucket = Math.floor(this.state.tick / every);
    if (this.lbCache.tick === bucket) return this.lbCache.value;
    const rows: [string, number][] = [];
    for (const pid in this.state.players) {
      const ent = this.playerEntity(pid);
      const v = ent ? ent[field] : undefined;
      rows.push([pid, typeof v === "number" ? Math.round(v) : 0]);
    }
    rows.sort((a, b) => b[1] - a[1] || (a[0] < b[0] ? -1 : 1));
    const value = rows.slice(0, 10);
    this.lbCache = { tick: bucket, value };
    return value;
  }

  // ---- world handle --------------------------------------------------------------------------

  private makeWorld(): World {
    const room = this;
    const { meta } = this.module;
    const defs = this.module.entities;
    return {
      meta,
      w: meta.world.w,
      h: meta.world.h,
      get tick() {
        return room.state.tick;
      },
      get time() {
        return room.state.time;
      },
      spawn(type, ownerId, overrides) {
        const def = defs.get(type);
        if (!def) throw new Error(`spawn: unknown entity type "${type}"`);
        const fields = def.spawn(this, ownerId, room.rng);
        const id = room.state.nextId++;
        const ent = { ...fields, ...overrides, id, type } as Entity;
        if (ownerId !== undefined) ent.owner = ownerId;
        checkEntity(ent, def);
        room.state.entities[id] = ent;
        if (ownerId !== undefined) {
          const p = room.state.players[ownerId];
          if (p && p.eid === undefined) p.eid = id;
        }
        return ent;
      },
      despawn(id) {
        const ent = room.state.entities[id];
        if (!ent) return;
        delete room.state.entities[id];
        if (ent.owner !== undefined) {
          const p = room.state.players[ent.owner];
          if (p && p.eid === id) p.eid = undefined;
        }
      },
      get(id) {
        return room.state.entities[id];
      },
      entities() {
        return room.liveEntities();
      },
      ofType(type) {
        return room.liveEntities().filter((e) => e.type === type);
      },
      count(type) {
        if (type === undefined) return Object.keys(room.state.entities).length;
        let n = 0;
        for (const e of room.liveEntities()) if (e.type === type) n++;
        return n;
      },
      query(x, y, radius) {
        const out: Entity[] = [];
        for (const e of room.spatial.queryCircle(x, y, radius)) {
          if (room.state.entities[e.id] === e) out.push(e);
        }
        return out;
      },
      playerEntity(playerId) {
        return room.playerEntity(playerId);
      },
      players() {
        return room.players();
      },
    };
  }
}

function checkEntity(ent: Entity, def: EntityDef): void {
  for (const f of ["x", "y", "r"] as const) {
    if (typeof ent[f] !== "number" || !Number.isFinite(ent[f])) {
      throw new Error(`spawn("${ent.type}"): ${f} must be a finite number`);
    }
  }
  for (const f of def.replicated) {
    const v = ent[f];
    if (v === undefined) continue;
    const t = typeof v;
    if (t !== "number" && t !== "string" && t !== "boolean") {
      throw new Error(`spawn("${ent.type}"): replicated field "${f}" must be a number, string or boolean`);
    }
  }
}

/** Numbers go over the wire with one decimal place: sub-pixel at zoom 1, far fewer changed fields. */
function quantise(v: unknown): unknown {
  return typeof v === "number" ? Math.round(v * 10) / 10 : v;
}
